/**
 * POS Parked Sales Modal
 * Shows list of parked/held sales that can be resumed
 * 
 * Backend returns parked_sales with fields:
 * - id, cart_items, customer, parked_by_name, parked_at, parked_at_fiji
 * - order_type, table_number, customer_discount, total, status, etc.
 */
import { X, ShoppingCart, User, Clock, Play, Trash2, Coffee, Lock, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function POSParkedSalesModal({
  isOpen,
  onClose,
  parkedSales,
  onResume,
  onDelete,
  onRelease,
  currentStaffId
}) {
  if (!isOpen) return null;
  
  // Format time ago
  const timeAgo = (dateString) => {
    if (!dateString) return 'Unknown';
    const now = new Date();
    const parkedTime = new Date(dateString);
    const diffMs = now - parkedTime;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };
  
  // Get cart items from backend format (cart_items) or frontend format (cart)
  const getCartItems = (sale) => {
    return sale.cart_items || sale.cart || [];
  };
  
  // Calculate sale total (prefer stored total)
  const getTotal = (sale) => {
    if (sale.total !== undefined) return sale.total;
    const items = getCartItems(sale);
    return items.reduce((sum, item) => sum + ((item.price || 0) * (item.quantity || 1)), 0);
  };
  
  // Check if sale is claimed by someone else
  const isClaimedByOther = (sale) => {
    return sale.status === 'claimed' && sale.claimed_by_id !== currentStaffId;
  };
  
  // Check if sale can be resumed by current user
  const canResume = (sale) => {
    return sale.can_resume || sale.status === 'active' || 
           (sale.status === 'claimed' && sale.claimed_by_id === currentStaffId);
  };
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-amber-500 text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-6 h-6" />
            <h2 className="text-xl font-bold">Parked Sales</h2>
            {parkedSales.length > 0 && (
              <span className="bg-white/20 px-2 py-0.5 rounded-full text-sm">
                {parkedSales.length}
              </span>
            )}
          </div>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-amber-600 rounded-full transition-colors"
            data-testid="close-parked-sales-modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {parkedSales.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <ShoppingCart className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium">No parked sales</p>
              <p className="text-sm">Parked sales will appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {parkedSales.map((sale) => {
                const cartItems = getCartItems(sale);
                const total = getTotal(sale);
                const claimedByOther = isClaimedByOther(sale);
                const resumable = canResume(sale);
                
                return (
                  <div 
                    key={sale.id}
                    className={`border rounded-xl p-4 transition-colors bg-gray-50 dark:bg-gray-800 ${
                      claimedByOther 
                        ? 'border-red-300 dark:border-red-700 opacity-75' 
                        : 'dark:border-gray-700 hover:border-amber-400 dark:hover:border-amber-500'
                    }`}
                    data-testid={`parked-sale-${sale.id}`}
                  >
                    {/* Claimed Banner */}
                    {claimedByOther && (
                      <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-xs mb-2 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded">
                        <Lock className="w-3 h-3" />
                        <span>In use by {sale.claimed_by_name}</span>
                      </div>
                    )}
                    
                    {/* Customer Info */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                          <User className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {sale.customer?.name || sale.customer?.first_name || 'Guest'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {sale.parked_at_fiji || timeAgo(sale.parked_at)} by {sale.parked_by_name || 'Staff'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-900 dark:text-white">
                          ${total.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {sale.item_count || cartItems.length} item{(sale.item_count || cartItems.length) !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    
                    {/* Items Preview */}
                    <div className="mb-3 text-sm text-gray-600 dark:text-gray-400">
                      {cartItems.slice(0, 3).map((item, idx) => (
                        <div key={idx} className="flex items-center gap-1">
                          {item.is_coffee_pass && <Coffee className="w-3 h-3 text-amber-500" />}
                          <span>{item.quantity || 1}x {item.name}</span>
                        </div>
                      ))}
                      {cartItems.length > 3 && (
                        <p className="text-gray-400 dark:text-gray-500 italic">
                          +{cartItems.length - 3} more items...
                        </p>
                      )}
                    </div>
                    
                    {/* Order Type Badge */}
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        sale.order_type === 'dine-in' 
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' 
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {sale.order_type === 'dine-in' ? `Dine-in Table ${sale.table_number || '?'}` : 'Takeaway'}
                      </span>
                      {sale.customer_discount?.discount && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                          {sale.customer_discount.discount}% Loyalty
                        </span>
                      )}
                      {sale.cashier_discount?.percent && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                          {sale.cashier_discount.percent}% Staff
                        </span>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-2">
                      {resumable ? (
                        <Button
                          onClick={() => onResume(sale.id)}
                          className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                          data-testid={`resume-parked-${sale.id}`}
                        >
                          <Play className="w-4 h-4 mr-2" />
                          Resume Sale
                        </Button>
                      ) : (
                        <Button
                          onClick={() => onRelease && onRelease(sale.id)}
                          variant="outline"
                          className="flex-1 text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                          data-testid={`release-parked-${sale.id}`}
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Force Release
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        onClick={() => onDelete(sale.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                        data-testid={`delete-parked-${sale.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="border-t dark:border-gray-700 p-4">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full"
            data-testid="close-parked-sales-btn"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
